import { useCallback, useRef } from "react";
import type { PttSidecarConfig } from "./pttSidecarClient";
import { usePttSidecar } from "./usePttSidecar";
import { browserSpeak } from "./voice/browserAudio";
import { usePttTransport } from "./voice/ptt/usePttTransport";
import { usePttTurns } from "./voice/ptt/usePttTurns";

export type { PttState, PttTurn } from "./voice/ptt/types";

type Deps = {
  runVoiceCommand: (query: string) => Promise<string>;
  sidecarConfig: PttSidecarConfig;
  fallbackTts?: boolean;
};

/**
 * PTT orchestrator: turn session + STT transport + TTS readback.
 *
 * STT implementations live under voice/ptt/ (aws stream, blob sidecar, browser).
 */
export function usePttVoice(deps: Deps) {
  const depsRef = useRef(deps);
  depsRef.current = deps;

  const { transcribe, speak, status: sidecarStatus } = usePttSidecar(deps.sidecarConfig);
  const sidecarStatusRef = useRef(sidecarStatus);
  sidecarStatusRef.current = sidecarStatus;

  const playReadback = useCallback(
    async (readback: string) => {
      if (!readback) return;
      const live = sidecarStatusRef.current === "online";
      if (live && !(depsRef.current.fallbackTts ?? false)) {
        try {
          await speak(readback);
          return;
        } catch {
          browserSpeak(readback);
          return;
        }
      }
      browserSpeak(readback);
    },
    [speak],
  );

  const session = usePttTurns({
    runVoiceCommand: deps.runVoiceCommand,
    playReadback,
  });

  const capture = usePttTransport(deps.sidecarConfig, session, transcribe, sidecarStatus);

  return {
    pttState: session.pttState,
    turnHistory: session.turnHistory,
    error: session.error,
    sidecarStatus,
    capture,
    runTextCommand: session.runTextCommand,
    clearHistory: session.clearHistory,
    isBusy: () => session.busyRef.current,
  };
}
